import { Component, inject, Optional } from '@angular/core';
import { ItemWithSelection } from '@app/shared/models/item-with-selection';
import {
  ActionResult,
  ListView,
  ListViewProviderDirective,
  NotificationService,
  Util,
  ViewState,
  ViewWindow,
  WindowRegistry
} from '@ntap/core';
import { combineLatest, EMPTY, map, Observable, of, switchMap } from 'rxjs';
import { OpenDialogItemsListActionComponent } from '../items-list-action/open-dialog-items-list-action.component';
import { CommonConfirmActionService } from '@app/services/common-confirm-action.service';
import {
  calculateDisabledStateAndTooltipForSelectedJediniceDostave,
  isDisabledActionForNerazvrstano
} from '@app/shared/util/jedinica-gradiva-security-utils';
import { withTooltipState } from '@app/shared/mixins/with-tooltip-state.mixin';

@Component({
  selector: 'app-dostava-action',
  templateUrl: '../common-confirm-action-template.html'
})
export class DostavaActionComponent extends withTooltipState(
  OpenDialogItemsListActionComponent
) {
  public commonConfirmActionService = inject(CommonConfirmActionService);

  key: string = 'requestDostavaJedinicaGradiva' + this.toString;

  constructor(
    protected override notificationService: NotificationService,
    protected override windowRegistry: WindowRegistry,
    @Optional() protected override window: ViewWindow,
    protected override listViewProvider: ListViewProviderDirective<ListView>
  ) {
    super(notificationService, windowRegistry, window, listViewProvider);
  }

  protected override initDisabledObservable(): void {
    const parentType = (this.listViewProvider.viewState as any)?.$parentType;

    this.isDisabled$ = combineLatest([
      this.listViewProvider.selectedItems$,
      this.listViewProvider.selectAll$
    ]).pipe(
      map(([selectedItems]) => {
        if (parentType === 'en~posjet') {
          this.setTooltip(this.action.description);
          return false;
        }

        const isDisabledBecauseSystem =
          isDisabledActionForNerazvrstano(selectedItems);
        const [publishable, tooltip] =
          calculateDisabledStateAndTooltipForSelectedJediniceDostave(
            selectedItems
          );

        if (isDisabledBecauseSystem) {
          this.setTooltip(
            'Granu nerazvrstanog gradiva nije moguće odabrati za dostavu.'
          );
        } else if (!publishable) {
          this.setTooltip(tooltip);
        } else {
          this.setTooltip(this.action.description);
        }

        return (
          !selectedItems ||
          selectedItems.length === 0 ||
          isDisabledBecauseSystem ||
          selectedItems.some(
            (j: any) => j['en~jedinica_gradiva~rezidualni_podaci']
          )
        );
      })
    );
  }

  protected override getInitialState(
    selectedItems: object[]
  ): ViewState & ItemWithSelection {
    const selected: object[] = [];

    const parentIdItem = this.listViewProvider.viewState as {
      $parentId: number;
    };

    const parentType = (this.listViewProvider.viewState as any)?.$parentType;

    if (parentType === 'en~posjet') {
      this.listViewProvider.selectAll$ = of(true);

      this.listViewProvider.setSelectedAllFlag(true);

      this.listViewProvider.selectedItems$.subscribe((items) => {
        selected.splice(0, 0, ...items);
      });
    } else {
      selected.splice(0, 0, ...Util.mapToBasicItemData(selectedItems));
      if (
        selected.length > 0 &&
        (selected[0] as any)['$itemType'] != 'en~jedinica_gradiva'
      ) {
        const newSelected = [];
        for (let i = 0; i < selected.length; i++) {
          const jedGradId = selectedItems[i] as {
            'en~jedinica_gradiva~id': number;
          };
          newSelected[i] = {
            $itemType: 'en~jedinica_gradiva',
            $mainType: 'en~jedinica_gradiva',
            'en~jedinica_gradiva~id': jedGradId['en~jedinica_gradiva~id'],
            'en~jedinica_gradiva~vstamp': 1
          };
        }
        selected.splice(0, selected.length, ...newSelected);
      }
    }
    return Object.assign(
      {},
      {
        $addPodjedinice: this.commonConfirmActionService.addPodjedinice,
        'earhiv~zahtjev_za_dostavom_gradiva~zahtjev_citaonica_id': (() => {
          if (parentType === 'en~zahtjev_za_narucivanje_gradiva') {
            return parentIdItem['$parentId'];
          }

          if (parentType === 'en~posjet') {
            return (
              (this.listViewProvider.viewState as any)?.$parent?.[
                'en~posjet~zahtjev_id'
              ] || null
            );
          }

          return null;
        })(),
        $selected: selected,
        $listName: this.listViewProvider.viewName,
        $filterCriteria: (this.listViewProvider?.viewState as any)?.[
          '$filterCriteria'
        ],
        $parentType: parentType,
        $parent: (this.listViewProvider.viewState as any)?.$parent
      }
    );
  }

  protected override doExecute(): Observable<ActionResult> {
    return this.commonConfirmActionService.confirm(this.key).pipe(
      switchMap(({ confirmed }) => {
        if (confirmed) {
          return super.doExecute();
        }
        return EMPTY;
      })
    );
  }
}
